/**
 * Screenshot Utility
 * Captures screenshots (PNG) of DOM elements
 * Used for capturing deployment summary before deploy confirmation
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Html2CanvasFn = (element: HTMLElement, options?: Record<string, unknown>) => Promise<HTMLCanvasElement>;

let html2canvasFn: Html2CanvasFn | null | undefined = undefined;

/**
 * Attempts to load html2canvas dynamically
 */
async function loadHtml2Canvas(): Promise<Html2CanvasFn | null> {
  if (html2canvasFn !== undefined) {
    return html2canvasFn as Html2CanvasFn | null;
  }

  try {
    const module = await import('html2canvas');
    html2canvasFn = module.default || module;
    return html2canvasFn as Html2CanvasFn;
  } catch (error) {
    console.info('Screenshot capture: html2canvas not available', error);
    html2canvasFn = null;
    return null;
  }
}

export interface ScreenshotResult {
  success: boolean;
  dataUrl?: string;
  error?: string;
}

/**
 * Captures a screenshot of a DOM element and triggers download
 * Uses a cloned element in an off-screen container to avoid layout constraints
 */
export async function captureElementScreenshot(
  element: HTMLElement | null,
  filename: string
): Promise<ScreenshotResult> {
  if (!element) {
    return { success: false, error: 'Element not found' };
  }

  const html2canvas = await loadHtml2Canvas();
  if (!html2canvas) {
    return { success: false, error: 'html2canvas not available' };
  }

  // Create an off-screen container
  const offscreenContainer = document.createElement('div');
  offscreenContainer.style.position = 'fixed';
  offscreenContainer.style.left = '-10000px';
  offscreenContainer.style.top = '0';
  offscreenContainer.style.width = 'auto';
  offscreenContainer.style.height = 'auto';
  offscreenContainer.style.overflow = 'visible';
  offscreenContainer.style.background = '#ffffff';
  offscreenContainer.style.zIndex = '-9999';
  
  try {
    // Clone the element
    const clone = element.cloneNode(true) as HTMLElement;
    
    // Remove all height and overflow constraints from the clone
    clone.style.overflow = 'visible';
    clone.style.overflowX = 'visible';
    clone.style.overflowY = 'visible';
    clone.style.maxHeight = 'none';
    clone.style.height = 'auto';
    clone.style.flex = 'none';
    clone.style.position = 'static';
    clone.style.minWidth = `${element.clientWidth}px`;
    clone.style.padding = '20px 24px';
    
    // Add to offscreen container
    offscreenContainer.appendChild(clone);
    document.body.appendChild(offscreenContainer);
    
    // Force a reflow
    clone.offsetHeight;
    
    // Get actual dimensions
    const width = clone.scrollWidth || clone.offsetWidth;
    const height = clone.scrollHeight || clone.offsetHeight;

    console.log('Capturing screenshot with dimensions:', { 
      originalScrollHeight: element.scrollHeight,
      originalClientHeight: element.clientHeight,
      cloneWidth: width,
      cloneHeight: height
    });

    const canvas = await html2canvas(clone, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      allowTaint: true,
      logging: false,
      width: width,
      height: height,
      windowWidth: width + 100,
      windowHeight: height + 100,
      scrollX: 0,
      scrollY: 0,
    });

    // Convert to data URL
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    
    // Create download link
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `${filename}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    console.log('Screenshot downloaded:', { 
      filename: `${filename}.png`,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height
    });

    return { success: true, dataUrl };
  } catch (error) {
    console.error('Screenshot capture failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  } finally {
    // Clean up offscreen container
    if (offscreenContainer.parentNode) {
      document.body.removeChild(offscreenContainer);
    }
  }
}

/**
 * Generates a sanitized filename
 */
export function generateScreenshotFilename(
  prefix: string,
  policyName?: string
): string {
  const parts: string[] = [prefix];
  
  if (policyName) {
    const sanitized = policyName
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .toLowerCase()
      .substring(0, 50);
    parts.push(sanitized);
  }
  
  const timestamp = new Date().toISOString()
    .replace(/:/g, '-')
    .replace(/\./g, '-')
    .substring(0, 19);
  parts.push(timestamp);
  
  return parts.join('_');
}

/**
 * Captures and downloads a deployment summary screenshot
 * @param containerRef - Reference to the summary container element
 * @param policyName - Name of the policy for the filename
 * @param viewType - 'dashboard' or 'tenant' view type
 */
export async function captureDeploymentSummary(
  containerRef: React.RefObject<HTMLElement | null>,
  policyName: string,
  viewType: 'dashboard' | 'tenant'
): Promise<ScreenshotResult> {
  const element = containerRef.current;
  
  if (!element) {
    console.warn('Summary container not available for capture');
    return { success: false, error: 'Summary container not found' };
  }

  const filename = generateScreenshotFilename(
    `${viewType}-policy-deployment-summary`,
    policyName
  );

  return captureElementScreenshot(element, filename);
}
